// Procedural soft jungle beat using Web Audio API
class JungleBeat {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.isPlaying = false;
    this.nextNoteTime = 0;
    this.currentBeat = 0;
    this.tempo = 92;
    this.timerID = null;
    this.volume = 0.75;
  }

  start() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.volume;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.currentBeat = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.05;
    this._schedule();
  }

  stop() {
    this.isPlaying = false;
    if (this.timerID) {
      clearInterval(this.timerID);
      this.timerID = null;
    }
  }

  _schedule() {
    const lookahead = 0.12;
    this.timerID = setInterval(() => {
      if (!this.isPlaying) return;
      while (this.nextNoteTime < this.ctx.currentTime + lookahead) {
        this._playBeat(this.currentBeat, this.nextNoteTime);
        const step = 60.0 / this.tempo / 4;
        this.nextNoteTime += step;
        this.currentBeat = (this.currentBeat + 1) % 16;
      }
    }, 25);
  }

  _playBeat(beat, time) {
    // Soft kick on 1 and 3
    if (beat === 0 || beat === 8) {
      this._kick(time);
    }
    // Gentle bongo on 2 and 4
    if (beat === 4 || beat === 12) {
      this._bongo(time, 280);
    }
    // Softer bongo ghost note
    if (beat === 6 || beat === 14) {
      this._bongo(time, 340, 0.15);
    }
    // Shaker on 8th notes
    if (beat % 2 === 0) {
      this._shaker(time, 0.3);
    }
    // Lighter shaker on off-beats
    if (beat % 2 === 1) {
      this._shaker(time, 0.18);
    }
    // Ambient tone pad every 4 bars (every 64 beats = 4 bars of 16)
    if (beat === 0 && Math.random() > 0.5) {
      this._ambientTone(time);
    }
  }

  _kick(time) {
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(100, time);
    osc.frequency.exponentialRampToValueAtTime(35, time + 0.18);
    gain.gain.setValueAtTime(1.0, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.22);
    osc.start(time);
    osc.stop(time + 0.25);
  }

  _bongo(time, freq = 280, vol = 0.6) {
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, time);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.6, time + 0.08);
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
    osc.start(time);
    osc.stop(time + 0.12);
  }

  _shaker(time, vol = 0.3) {
    const len = Math.floor(this.ctx.sampleRate * 0.025);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const hp = this.ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 7000;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.025);
    src.connect(hp);
    hp.connect(gain);
    gain.connect(this.masterGain);
    src.start(time);
  }

  _ambientTone(time) {
    const notes = [261.6, 329.6, 392.0, 440.0];
    const freq = notes[Math.floor(Math.random() * notes.length)];
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.25, time + 0.8);
    gain.gain.linearRampToValueAtTime(0, time + 2.5);
    osc.start(time);
    osc.stop(time + 2.6);
  }

  setVolume(vol) {
    this.volume = vol;
    if (this.masterGain) {
      this.masterGain.gain.setValueAtTime(vol, this.ctx.currentTime);
    }
  }

  toggle() {
    if (this.isPlaying) {
      this.stop();
    } else {
      this.start();
    }
    return this.isPlaying;
  }

  destroy() {
    this.stop();
    if (this.ctx) {
      this.ctx.close().catch(() => {});
      this.ctx = null;
    }
  }
}

let instance = null;

export function getJungleBeat() {
  if (!instance) {
    instance = new JungleBeat();
  }
  return instance;
}

export function destroyJungleBeat() {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}
